import {
  ClothingItem,
  BodyProfile,
  Outfit,
  AIAnalysisResult,
  AITryOnReview,
  AppSettings,
  User,
  CategoryType,
} from '../types';

const TOKEN_KEY = 'fitcheck_session_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers || {});
  const token = getToken();

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  if (!(options.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(path, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    clearToken();
    window.dispatchEvent(new CustomEvent('auth:expired'));
    throw new Error('Session expired or login required.');
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || `Request failed with status ${response.status}`);
  }

  return data as T;
}

// Auth & Multi-Account API
export const api = {
  auth: {
    checkSetup: () => request<{ is_setup: boolean; app_name: string }>('/api/auth/setup'),
    setup: (payload: { username: string; password: string }) =>
      request<{ success: boolean; token: string; user: User }>('/api/auth/setup', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    register: (payload: { username: string; password: string }) =>
      request<{ success: boolean; token: string; user: User }>('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    login: (payload: { username: string; password: string }) =>
      request<{ success: boolean; token: string; user: User }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    logout: () =>
      request<{ success: boolean }>('/api/auth/logout', {
        method: 'POST',
      }),
    me: () => request<{ user: User }>('/api/auth/me'),
    getProfile: () =>
      request<{
        user: {
          id: string;
          username: string;
          created_at: string;
          stats: {
            clothes_count: number;
            outfits_count: number;
            body_profiles_count: number;
          };
        };
      }>('/api/auth/profile'),
    updatePassword: (payload: { current_password: string; new_password: string }) =>
      request<{ success: boolean; message: string }>('/api/auth/profile', {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),
  },

  clothes: {
    list: (params?: {
      category?: string;
      brand?: string;
      size?: string;
      season?: string;
      occasion?: string;
      search?: string;
      favorite?: boolean;
      sort?: string;
    }) => {
      const q = new URLSearchParams();
      if (params?.category) q.set('category', params.category);
      if (params?.brand) q.set('brand', params.brand);
      if (params?.size) q.set('size', params.size);
      if (params?.season) q.set('season', params.season);
      if (params?.occasion) q.set('occasion', params.occasion);
      if (params?.search) q.set('search', params.search);
      if (params?.favorite) q.set('favorite', 'true');
      if (params?.sort) q.set('sort', params.sort);
      return request<{ clothes: ClothingItem[]; brands: string[]; sizes: string[] }>(
        `/api/clothes?${q.toString()}`
      );
    },
    get: (id: string) => request<{ item: ClothingItem }>(`/api/clothes/${id}`),
    create: (payload: Partial<ClothingItem>) =>
      request<{ success: boolean; item: ClothingItem }>('/api/clothes', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    update: (id: string, payload: any) =>
      request<{ success: boolean; message: string }>(`/api/clothes/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),
    toggleFavorite: (id: string) =>
      request<{ success: boolean }>(`/api/clothes/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ action: 'toggle_favorite' }),
      }),
    logWear: (id: string) =>
      request<{ success: boolean }>(`/api/clothes/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ action: 'log_wear' }),
      }),
    delete: (id: string) =>
      request<{ success: boolean }>(`/api/clothes/${id}`, {
        method: 'DELETE',
      }),
  },

  profiles: {
    list: () => request<{ profiles: BodyProfile[] }>('/api/profiles'),
    create: (payload: {
      name: string;
      image_data: string;
      height_cm?: number;
      body_type?: string;
      is_default?: boolean;
    }) =>
      request<{ success: boolean; profile: BodyProfile }>('/api/profiles', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    setDefault: (id: string) =>
      request<{ success: boolean }>(`/api/profiles/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ action: 'set_default' }),
      }),
    delete: (id: string) =>
      request<{ success: boolean }>(`/api/profiles/${id}`, {
        method: 'DELETE',
      }),
  },

  outfits: {
    list: () => request<{ outfits: Outfit[] }>('/api/outfits'),
    create: (payload: Partial<Outfit>) =>
      request<{ success: boolean; outfit: Outfit }>('/api/outfits', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    logWear: (id: string) =>
      request<{ success: boolean }>(`/api/outfits/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ action: 'log_wear' }),
      }),
    rate: (id: string, rating: number) =>
      request<{ success: boolean }>(`/api/outfits/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ action: 'rate', rating }),
      }),
    delete: (id: string) =>
      request<{ success: boolean }>(`/api/outfits/${id}`, {
        method: 'DELETE',
      }),
  },

  ai: {
    analyze: (payload: { image_front: string; image_back?: string; gemini_api_key?: string }) =>
      request<{ success: boolean; provider: string; analysis: AIAnalysisResult }>('/api/ai/analyze', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    tryOn: (payload: {
      body_image: string;
      items: Array<{
        category: CategoryType;
        name: string;
        brand: string;
        size: string;
        color?: string;
        image_front?: string;
        image_cropped?: string;
      }>;
      gemini_api_key?: string;
    }) =>
      request<{ success: boolean; provider: string; tryon_image?: string; review: AITryOnReview }>('/api/ai/try-on', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
  },

  settings: {
    get: () => request<AppSettings>('/api/settings'),
    save: (payload: {
      gemini_api_key?: string;
      clear_key?: boolean;
      preferred_model?: string;
      auto_crop?: boolean;
    }) =>
      request<{ success: boolean; message: string }>('/api/settings', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    testGeminiKey: (key?: string) =>
      request<{ success: boolean; message: string; models_found: string[] }>('/api/settings', {
        method: 'POST',
        body: JSON.stringify({ action: 'test_gemini', gemini_api_key: key }),
      }),
  },
};
